import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  IEmployeeAppInboxResponse,
  IExperienceDenormalizedV2
} from '@dadohr/shared/domainTypes/experience';
import { IOrganizationDenormalized } from '@dadohr/shared/domainTypes/org';
import { IUserDenormalized } from '@dadohr/shared/domainTypes/user';
import { Theme } from '@emotion/react';
import styled from '@emotion/styled';
import { Button } from '@material-ui/core';
import { ExternalLink } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { OrgLogo } from '../../../../../../components/OrgLogo';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { FlexContainer } from '../../../../../../layout/Flex';
import { interpolate } from '../../../../../../services/set/interpolation';
import { getDashboardUrl, getInboxUrl } from '../../services/routes';
import { MaybeImpersonationBanner } from '../ImpersonationBanner';

export const TOP_NAV_HEIGHT = 56;
export const TOP_NAV_PADDING_TOP = 1;
export const TOP_NAV_PADDING_BOTTOM = 1;
export const TOP_NAV_COLOR = (t: Theme) =>
  t.custom.colors.headerBackgroundDark.main;

const getBestHeaderBackgroundNavColor = (theme: Theme) => {
  // The header background doesn't define its own contrast text - it typically uses a computed value
  // IF it's the same as the primary main color though, we can opt into the custom user-defined color
  // for primary instead.
  const cColors = theme.custom.colors;
  if (
    cColors.headerBackgroundDark.main === cColors.primary.main &&
    cColors.primary.contrastText
  ) {
    return cColors.primary.contrastText;
  }
  return theme.palette.getContrastText(TOP_NAV_COLOR(theme));
};

export const TopNavBar = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: TOP_NAV_COLOR(p.theme),
  color: getBestHeaderBackgroundNavColor(p.theme),
  paddingLeft: p.theme.spacing(5),
  paddingRight: p.theme.spacing(5),
  paddingTop: p.theme.spacing(TOP_NAV_PADDING_TOP),
  paddingBottom: p.theme.spacing(TOP_NAV_PADDING_BOTTOM),
  width: '100%',
  position: 'sticky',
  top: 0,
  zIndex: 2,
  height: TOP_NAV_HEIGHT
}));

export const TopNav = ({
  org
}: {
  org: IOrganizationDenormalized;
  // TODO accessTokens to enable routing
}) => {
  // TODO make sticky
  return (
    <TopNavBar>
      <div>
        <OrgLogo d={org} />
      </div>
    </TopNavBar>
  );
};

export const TopNavV2: React.FC<{
  org: IOrganizationDenormalized;
  navLinks?: React.ReactNode;
}> = ({ org, children, navLinks }) => {
  // TODO make sticky
  return (
    <TopNavBar>
      <FlexContainer spacing={3}>
        <OrgLogo d={org} inverted />
        {navLinks}
      </FlexContainer>
      {children}
    </TopNavBar>
  );
};

const AdditionalLinks = ({
  userAccessToken
}: {
  userAccessToken: string | null;
}) => {
  const { t } = useUnsafeTranslation();
  const urls = useMemo(() => {
    if (!userAccessToken) {
      return null;
    }
    return {
      dashboard: getDashboardUrl(userAccessToken),
      inbox: getInboxUrl(userAccessToken)
    };
  }, [userAccessToken]);

  if (!urls) {
    return null;
  }

  return (
    <>
      <Link to={urls.dashboard}>{t('Experiences')}</Link>
      <Link to={urls.inbox}>{t('Inbox')}</Link>
    </>
  );
};

const ViewInAdminAppButton = ({ url }: { url: string }) => {
  const { t } = useUnsafeTranslation();
  return (
    <Button
      startIcon={<ExternalLink size={12} />}
      variant="contained"
      size="small"
      href={url}
      target="_blank"
      color="default"
    >
      {t('View in admin app')}
    </Button>
  );
};

export const TopNavBarV2ForInstance = ({
  instance,
  searchComponent
}: {
  instance: Doc<IExperienceDenormalizedV2>;
  searchComponent?: React.ReactNode;
}) => {
  return (
    <>
      <MaybeImpersonationBanner user={instance.data.user} />
      <TopNavV2
        org={instance.data.org}
        navLinks={
          <AdditionalLinks
            userAccessToken={instance.data.userAccessToken || null}
          />
        }
      >
        {
          <FlexContainer>
            {instance.data.links.adminApp?.instance && (
              <ViewInAdminAppButton
                url={instance.data.links.adminApp.instance}
              />
            )}
            {searchComponent}
          </FlexContainer>
        }
      </TopNavV2>
    </>
  );
};

export const TopNavBarV2ForExperienceHub = <
  X extends {
    userAccessToken: string;
    user: IUserDenormalized;
    org: IOrganizationDenormalized;
    linkTemplates: IEmployeeAppInboxResponse['linkTemplates'];
  }
>({
  data,
  searchComponent,
  linkInterpolationData
}: {
  data: X;
  searchComponent?: React.ReactNode;
  linkInterpolationData?: {
    templateId: string;
    instanceId?: string;
  } | null;
}) => {
  const { userAccessToken } = data;
  const adminAppUrl = useMemo(() => {
    if (!data.linkTemplates) {
      return null;
    }
    if (!linkInterpolationData) {
      return data.linkTemplates.adminApp.list;
    }
    if (!linkInterpolationData.instanceId) {
      return interpolate(
        data.linkTemplates.adminApp.template,
        linkInterpolationData
      );
    }
    return interpolate(
      data.linkTemplates.adminApp.instance,
      linkInterpolationData
    );
  }, [data.linkTemplates, linkInterpolationData]);
  return (
    <>
      <MaybeImpersonationBanner user={data.user} />
      <TopNavV2
        org={data.org}
        navLinks={<AdditionalLinks userAccessToken={userAccessToken} />}
      >
        <FlexContainer>
          {adminAppUrl && <ViewInAdminAppButton url={adminAppUrl} />}
          {searchComponent}
        </FlexContainer>
      </TopNavV2>
    </>
  );
};
